import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Box,
} from "@mui/material";
import { EditOutlined, DeleteOutlined } from "@mui/icons-material";
import { GridColDef, GridRowId } from "@mui/x-data-grid-premium";
import { SelectOption } from "../types";
import { EditFormModal } from "./EditFormModal";

export interface BaseContextMenuProps {
  open: boolean;
  event: MouseEvent | null;
  closeContextMenu: () => void;
  selectedRows: any[];
  onGridRefresh?: () => void;

  // Edit form props (optional — graceful fallback if not provided)
  columns?: GridColDef[];
  tableName?: string;
  apiRef?: React.RefObject<any>;
  onSingleUpdate?: (
    rowId: GridRowId,
    data: Record<string, any>
  ) => Promise<{ success: boolean; error?: string }>;
  onBulkSet?: (
    ids: (string | number)[],
    fields: Record<string, any>
  ) => Promise<{ success: boolean; error?: string }>;
  fieldOptions?: Record<string, SelectOption[]>;
  getRowId?: (row: any) => string | number;
  defaultEditFieldVisibility?: Record<string, boolean>;
  onDelete?: (
    rowIds: (string | number)[],
    onProgress?: (completed: number, total: number) => void
  ) => Promise<{ success: boolean; error?: string }>;
  getDeleteBlockReason?: (row: any) => string | undefined;

  // Extension points for custom context menu wrappers
  children?: React.ReactNode;
  extraNodes?: React.ReactNode;
  activeCustomModal?: React.ReactNode | null;
}

export const BaseContextMenu: React.FC<BaseContextMenuProps> = ({
  open,
  event,
  closeContextMenu,
  selectedRows,
  onGridRefresh: _onGridRefresh,
  columns,
  tableName,
  apiRef,
  onSingleUpdate,
  onBulkSet,
  fieldOptions,
  getRowId,
  defaultEditFieldVisibility,
  onDelete,
  getDeleteBlockReason,
  children,
  extraNodes,
  activeCustomModal,
}) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);

  const hasEditFormProps =
    columns && tableName && apiRef && onSingleUpdate && onBulkSet && getRowId;

  const blockedDeleteReasons = getDeleteBlockReason
    ? Array.from(
        new Set(
          selectedRows
            .map((row) => getDeleteBlockReason(row))
            .filter((reason): reason is string => !!reason)
        )
      )
    : [];

  // Keep base invisible until needed; allow modals to outlive open=false
  if (!open && !activeCustomModal && !editModalOpen && !deleteModalOpen)
    return null;

  const handleDeleteConfirm = async () => {
    if (!onDelete || !getRowId) return;
    const ids = selectedRows.map(getRowId);
    setDeleting(true);
    setDeleteProgress(0);
    const result = await onDelete(ids, (completed, total) => {
      setDeleteProgress(Math.round((completed / total) * 100));
    });
    setDeleting(false);
    setDeleteModalOpen(false);
    if (result.success) closeContextMenu();
  };

  // Custom modal from wrapper takes full control
  if (activeCustomModal) {
    return <>{activeCustomModal}</>;
  }

  if (deleteModalOpen) {
    const rowCount = selectedRows.length;
    return (
      <Dialog
        open={deleteModalOpen}
        onClose={() => { if (!deleting) setDeleteModalOpen(false); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {rowCount > 1 ? `Delete ${rowCount} Rows?` : "Delete Row?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This action cannot be undone.
          </Typography>
          {deleting && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={deleteProgress} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                Deleting… {deleteProgress}%
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteModalOpen(false)}
            variant="outlined"
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (editModalOpen) {
    if (hasEditFormProps) {
      return (
        <EditFormModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            closeContextMenu();
          }}
          columns={columns}
          selectedRows={selectedRows}
          tableName={tableName}
          apiRef={apiRef}
          onSingleUpdate={onSingleUpdate}
          onBulkSet={onBulkSet}
          fieldOptions={fieldOptions}
          getRowId={getRowId}
          defaultFieldVisibility={defaultEditFieldVisibility}
        />
      );
    }
    // Graceful fallback when edit form props are not yet wired
    return null;
  }

  const menuPosition = event
    ? { top: event.pageY - window.scrollY, left: event.pageX }
    : { top: 0, left: 0 };

  return (
    <>
      <Menu
        open={open}
        onClose={closeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={menuPosition}
      >
        {selectedRows.length > 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="textSecondary">
              ({selectedRows.length}) selected
            </Typography>
          </MenuItem>
        )}
        {selectedRows.length > 0 && <Divider />}

        <MenuItem onClick={() => setEditModalOpen(true)}>
          <ListItemIcon>
            <EditOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            Edit Row{selectedRows.length > 1 ? "s" : ""}
          </ListItemText>
        </MenuItem>

        {onDelete && (
          <MenuItem
            onClick={() => setDeleteModalOpen(true)}
            disabled={blockedDeleteReasons.length > 0}
          >
            <ListItemIcon>
              <DeleteOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              Delete Row{selectedRows.length > 1 ? "s" : ""}
              {blockedDeleteReasons.length > 0 && (
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  {blockedDeleteReasons.join(", ")}
                </Typography>
              )}
            </ListItemText>
          </MenuItem>
        )}

        {children}
      </Menu>

      {extraNodes}
    </>
  );
};
